import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { withCookie, withURL, isBrowser } from '@lucid/utils';
import { useNavMenu, useNavButtons, useSiteMetadata, useSmartlingStrings } from '@lucid/hooks';

import type { ComponentImage } from '../../types/generics';
import NavSection, { type NavSectionOptions } from '../../modules/nav-section/nav-section';
import useAbTest from '../../modules/ab-testing/hooks/use-ab-test';
import LayoutContext from '../context/layout-context';
import type { Lucid } from '../../types/types';

export type NavbarProps = {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
  /** The translations to display in the LanguageSwitcher */
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
  /** The main_nav id for the global nav */
  globalMenuId?: number;
  /** The main_nav id for the site nav */
  siteMenuId: number;
  /** The default page urls for the current site */
  defaultPageUrls?: Record<string, string>;
} & NavSectionOptions;

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({
  buttonAriaLabel,
  nodeTranslations = [],
  shouldHideSignUpButton,
  hasGlobalNav,
  siteMenuId,
  globalMenuId,
  defaultPageUrls,
  ...rest
}) => {
  const { shortCode, product, urlLangPrefix, pageType } = useContext(LayoutContext);
  const { product: siteProduct } = useSiteMetadata();
  // TODO: if/when they want this back, comment back in
  // const shouldUseDemoVideoLink = shortCode === 'en' && ['chart', 'spark'].includes(product);
  const signUpFreeABTest = useAbTest('FY26_LucidCoSignUpFree');
  const isLoggedIn = !!withCookie.get('username');
  const navButtonParams = {
    isLoggedIn,
    shouldHideSignUpButton,
    languageKey: shortCode,
    product,
    defaultPageUrls,
    // TODO: if/when they want this back, comment back in
    // shouldUseDemoVideoLink,
    shouldUseCoLoginB: signUpFreeABTest?.branch === 'T_B',
    shouldUseCoLoginC: signUpFreeABTest?.branch === 'T_C',
  };

  const siteNavButtons = useNavButtons({ ...navButtonParams });
  const globalNavButtons = useNavButtons({
    ...navButtonParams,
    isGlobalNav: true,
  });

  const siteNavMenu = useNavMenu(siteMenuId, shortCode);
  const globalNavMenu = useNavMenu(globalMenuId, shortCode)?.filter(
    // 'Contact sales' button is added in to global nav menu in the CMS...
    // ...but under certain conditions (shouldUseDemoVideoLink), it will also be added via the site nav buttons hook, so we don't want to duplicate it
    item => !siteNavButtons.some(button => button.final_url === item.link?.final_url),
  );

  const withSmartling = useSmartlingStrings({
    keys: ['mktg_product_grid_aria_label'],
    shortCode,
  });

  const shouldInitialHideGlobalNav =
    ['consideration-organic', 'paid-search-lander', 'ppc-search-lander'].includes(pageType || '') && hasGlobalNav;

  if (isBrowser() && shouldInitialHideGlobalNav) {
    window.scrollTo(0, 48);
  }

  // TODO: These can likely be moved to nav-logo.tsx, along with logoLink logic
  const logos: Partial<Record<Lucid.Site.Product, ComponentImage>> = {
    chart: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidchart.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidchart' }) || 'Go to homepage',
    },
    co: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/images/LucidSoftwareLogo.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucid' }) || 'Go to homepage',
    },
    edu: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucid-for-education.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucid for Education' }) || 'Go to homepage',
    },
    scale: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidscale.com/lucid/logos/lucidscale.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidscale' }) || 'Go to homepage',
    },
    spark: {
      imageSrc: 'https://cdn-cashy-static-assets.lucidspark.com/lucid/logos/lucidspark.svg',
      imageAlt: withSmartling('mktg_product_grid_aria_label', { product: 'Lucidspark' }) || 'Go to homepage',
    },
  };

  const nestedProducts = {
    chart: 'lucidchart',
    spark: 'lucidspark',
    scale: 'lucidscale',
  };
  const isNestedProduct = product in nestedProducts && product !== siteProduct;
  const nestedProduct = isNestedProduct ? nestedProducts[product] : undefined;
  const pathPrefix = nestedProduct || '';

  return (
    <NavSection
      {...rest}
      buttonAriaLabel={buttonAriaLabel}
      globalNav={{
        buttons: globalNavButtons,
        items: globalNavMenu,
      }}
      hasGlobalNav={hasGlobalNav}
      nodeTranslations={nodeTranslations}
      siteNav={{
        buttons: siteNavButtons,
        items: siteNavMenu,
        logo: logos[product ?? 'co'],
        logoLink: withURL('/', {
          urlLangPrefix,
          product,
          pageType,
          prefix: pathPrefix,
          willExcludePrefix: false,
        }),
      }}
    />
  );
};

export default Navbar;
